import icn_line_chart from 'assets/images/icons/icn_line_chart.png';

import React, { useMemo, useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from "@mui/material/styles";
import { createColumnHelper } from "@tanstack/react-table";
import { v4 as uuidv4 } from "uuid";

import useTableInstance from "hooks/useTableInstance";
import useDataSourceManager from "hooks/useDataSourceManager";

import CollapseCard from "components/Collapse";
import TableField from "components/Table";
import AuthTrendChart from 'components/AuthTrendChart';

import EllipsisTooltipCell from "utils/ellipsisTooltipCell";

import Api from "pages/AInsight/ServiceExperience/util/api";

const DEFAULT_DISPLAY_COUNT = 2;

const AuthTimeDrawer = ({ chartData, auth }) => {
  const theme = useTheme();
  const { sendBatch } = useDataSourceManager();
  const columnHelper = useMemo(() => createColumnHelper(), []);
  const [loading, setLoading] = useState(true);
  const [showAllAp, setShowAllAp] = useState(false);
  const [showAllSsid, setShowAllSsid] = useState(false);
  const [apData, setApData] = useState([]);
  const [ssidData, setSsidData] = useState([]);
  const [requestUuids] = useState({
    devices: uuidv4(),
    ssids: uuidv4(),
  });

  const fetchData = async () => {
    if (!auth) return;
    setLoading(true);
    const api = Api.init(auth);
    const devicesParams = api.getConnectTimeDevicesParams();
    const ssidsParams = api.getConnectTimeSsidsParams();
    const { getSendBatchData } = await sendBatch([
      { ...devicesParams, requestUuid: requestUuids.devices },
      { ...ssidsParams, requestUuid: requestUuids.ssids },
    ]);
    const devicesRes = getSendBatchData({ requestUuid: requestUuids.devices })?.data;
    const ssidsRes = getSendBatchData({ requestUuid: requestUuids.ssids })?.data;
    if (devicesRes?.data) setApData(devicesRes.data);
    if (ssidsRes?.data) setSsidData(ssidsRes.data);
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [auth]);

  const apTableData = useMemo(() =>
    showAllAp ? apData : apData.slice(0, DEFAULT_DISPLAY_COUNT),
    [showAllAp, apData]);

  const ssidTableData = useMemo(() =>
    showAllSsid ? ssidData : ssidData.slice(0, DEFAULT_DISPLAY_COUNT),
    [showAllSsid, ssidData]);

  const apColumns = useMemo(() => [
    columnHelper.accessor("deviceName", {
      header: "Device Name",
      cell: (info) => <EllipsisTooltipCell text={info.getValue()} />,
    }),
    columnHelper.accessor("events", {
      header: "Samples",
      cell: (info) => info.getValue().toLocaleString(),
    }),
    columnHelper.accessor("P95sec", {
      header: "95% Connect Within(Second)",
      cell: (info) => `${info.getValue()}s`,
    }),
  ], [columnHelper]);

  const ssidColumns = useMemo(() => [
    columnHelper.accessor("ssidName", {
      header: "SSID Name",
      cell: (info) => <EllipsisTooltipCell text={info.getValue()} />,
    }),
    columnHelper.accessor("events", {
      header: "Samples",
      cell: (info) => info.getValue().toLocaleString(),
    }),
    columnHelper.accessor("P95sec", {
      header: "95% Connect Within(Second)",
      cell: (info) => `${info.getValue()}s`,
    }),
  ], [columnHelper]);

  const apTable = useTableInstance({
    tableName: "topApAuthTimeDrawerTable",
    columns: apColumns,
    data: apTableData,
    enableSorting: false,
    enablePagination: false,
  });

  const ssidTable = useTableInstance({
    tableName: "topSsidAuthTimeDrawerTable",
    columns: ssidColumns,
    data: ssidTableData,
    enableSorting: false,
    enablePagination: false,
  });

  if (!chartData) return null;

  const showMoreAp = apData.length > DEFAULT_DISPLAY_COUNT;
  const showMoreSsid = ssidData.length > DEFAULT_DISPLAY_COUNT;

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box component="img" src={icn_line_chart} sx={{ width: '20px', height: '20px', mr: 1.5 }} />
        <Typography sx={{ fontWeight: 'bold', color: '#172664', fontSize: '18px' }}>
          Captive Portal Authentication Time
        </Typography>
      </Box>
      <CollapseCard title="Captive Portal Connect Time">
        <Box sx={{ height: '250px', width: '100%' }}>
          <AuthTrendChart data={chartData.series} height="100%" />
        </Box>
      </CollapseCard>
      <CollapseCard title="Top APs by Slowest Connect Time">
        <Box sx={{ width: "100%" }}>
          <TableField {...apTable} loading={loading} />
          {showMoreAp && (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Typography
                onClick={() => setShowAllAp(prev => !prev)}
                variant="text"
                sx={{
                  minWidth: 0, padding: 0, color: theme.palette.primary.greySecound, cursor: "pointer",
                }}
              >
                {showAllAp ? "" : "More..."}
              </Typography>
            </Box>
          )}
        </Box>
      </CollapseCard>
      <CollapseCard title="Top SSIDs by Slowest Connect Time">
        <Box sx={{ width: "100%" }}>
          <TableField {...ssidTable} loading={loading} />
          {showMoreSsid && (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Typography
                onClick={() => setShowAllSsid(prev => !prev)}
                variant="text"
                sx={{
                  minWidth: 0, padding: 0, color: theme.palette.primary.greySecound, cursor: "pointer",
                }}
              >
                {showAllSsid ? "" : "More..."}
              </Typography>
            </Box>
          )}
        </Box>
      </CollapseCard>
    </Box>
  );
};

export default AuthTimeDrawer;
